import React, { useState, useEffect } from 'react';
import './App.css';

const API_URL = ''; // Same origin

function App() {
  const [token, setToken] = useState(localStorage.getItem('lapcam_token'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [loading, setLoading] = useState(false);

  // Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const resp = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      const data = await resp.json();
      
      if (!resp.ok) {
        throw new Error(data.error || 'Login failed');
      }
      
      setToken(data.token);
      localStorage.setItem('lapcam_token', data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('lapcam_token');
    setCameras([]);
    setSelectedCamera(null);
  };

  // Fetch cameras
  useEffect(() => {
    if (!token) return;
    
    const fetchCameras = async () => {
      try {
        const resp = await fetch(`${API_URL}/api/cameras`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (resp.ok) {
          const data = await resp.json();
          setCameras(data);
        } else {
          handleLogout();
        }
      } catch (err) {
        console.error('Failed to fetch cameras:', err);
      }
    };
    
    fetchCameras();
  }, [token]);

  // If no token, show login
  if (!token) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h1>📹 LapCam</h1>
          <form onSubmit={handleLogin}>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading}
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
            {error && <div className="error">{error}</div>}
            <button type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Show dashboard with camera list
  return (
    <div className="app">
      <header className="header">
        <h1>📹 LapCam Dashboard</h1>
        <button onClick={handleLogout} className="logout-btn">Logout</button>
      </header>
      
      <div className="dashboard">
        <div className="camera-list">
          <h2>Cameras ({cameras.length})</h2>
          {cameras.map(camera => (
            <div
              key={camera.id}
              className={`camera-item ${selectedCamera?.id === camera.id ? 'selected' : ''}`}
              onClick={() => setSelectedCamera(camera)}
            >
              <div className="camera-name">{camera.name}</div>
              <div className="camera-status">
                {camera.status === 'connected' ? '🟢 Live' : '⚪ Offline'}
              </div>
              <div className="camera-last-seen">
                {camera.last_seen ? new Date(camera.last_seen).toLocaleString() : 'Never'}
              </div>
            </div>
          ))}
        </div>
        
        <div className="viewer">
          {selectedCamera ? (
            <>
              <h2>{selectedCamera.name}</h2>
              <StreamViewer cameraId={selectedCamera.id} token={token} />
            </>
          ) : (
            <div className="no-camera">
              <p>Select a camera to view</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StreamViewer({ cameraId, token }) {
  const [status, setStatus] = useState('Connecting...');
  const imgRef = React.useRef(null);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    
    const streamUrl = `${API_URL}/api/stream/${cameraId}/view?token=${token}`;
    
    img.onload = () => {
      setStatus('✅ Live');
    };
    
    img.onerror = () => {
      setStatus('❌ Connection failed - retrying...');
      setTimeout(() => {
        img.src = streamUrl + '&t=' + Date.now();
      }, 2000);
    };
    
    img.src = streamUrl;
    
    return () => {
      img.src = '';
    };
  }, [cameraId, token]);

  return (
    <div className="stream-viewer">
      <div className="status">{status}</div>
      <img ref={imgRef} alt="Live stream" />
    </div>
  );
}

export default App;
